import { computed } from "vue";
import {
  formattedPrice,
  setLocalStorage,
  getLocalStorage,
  setCartQtyIcon,
  updateUrl,
  scrollToSection,
  empty,
} from "../../../helpers";
import { deliveryFields } from "../../../form";
import store from "../../store";
import useGlobal from "../../useGlobal";
import useRoutes from "../../useRoutes";
import useModal from "../../useModal";
import useOrderService from "../../packages/views/order-service/useOrderService";
import useDiscountCoupon from "../../packages/views/discount-coupon/useDiscountCoupon";

export default function useCart() {
  // variables
  const defaultImageUrl = "/img/product/photo.png";
  const { getData } = useGlobal();
  const { deleteProductModal } = useModal();
  const { cartIndex } = useRoutes();
  const { getOrderServicePrice } = useOrderService(); // [PACKAGE] - order service
  const { getDiscountCouponPrice, applySpecialDiscount } = useDiscountCoupon(); // [PACKAGE] - discount coupon

  // computed
  const discountPrice = computed(() => getComputedDiscountCouponPrice());

  const totalPrice = computed(() => getTotalPrice(true));

  const productPrice = computed(() => priceForFreeDelivery());

  /*
        Methods
     */
  //
  function priceForFreeDelivery() {
    const discountCoupon = store.getters["discountCoupon/getData"];

    let price = getProductPrice();

    if (discountCoupon) {
      price -= discountPrice.value;
    }

    price += getOrderServicePrice();

    return price;
  }
  //
  function getComputedDiscountCouponPrice() {
    const price = getProductPrice();

    return getDiscountCouponPrice(price);
  }

  function getImage(product) {
    if (product.files.croppedFile) {
      return product.files.croppedFile.url;
    }
    if (product.files.croppedCollage1) {
      return product.files.croppedCollage1.url;
    }
    if (product.image_url) {
      return product.image_url;
    }

    return defaultImageUrl;
  }

  // Photo - layout photos
  function layoutPhotoCount(product) {
    return Number(product.attribute_values.photoLayout.preferences.photoCount);
  }

  // price = productPrices + productServices + orderServices + shipping + payment - discountCoupon
  function getTotalPrice(isFormatted) {
    const hasFreeShipping = store.getters["cart/getHasFreeShipping"];
    const discountCoupon = store.getters["discountCoupon/getData"];
    const cartData = store.getters["cart/getCartData"];
    let price = 0;
    // products price
    price += getProductPrice();
    // [PACKAGE] - discount coupon
    if (discountCoupon) {
      price -= discountPrice.value;
    }
    // [PACKAGE] - order service
    price += getOrderServicePrice();
    // shipping
    if (cartData.shipping) {
      if (!hasFreeShipping || !cartData.shipping.can_free) {
        price += cartData.shipping.price;
      }
    }

    if (cartData.payment) {
      price += cartData.payment.price;
    }

    if (price < 0) {
      price = 0;
    }

    return isFormatted ? formattedPrice(price) : price;
  }

  // price = productPrice + productServices
  function getProductPrice() {
    const { products } = store.getters["cart/getCartData"];
    let price = 0;

    for (let i = 0; i < products.length; i++) {
      price += products[i].price * products[i].qty;
    }

    return Number(price);
  }

  function deleteProduct(product) {
    deleteProductModal((result) => {
      // remove product from cart
      store.commit("cart/REMOVE_PRODUCT_FROM_CART", product);

      setCartLocalStorage();

      setCartQtyIcon();

      // update discount
      applySpecialDiscount();
    });
  }

  function setCartLocalStorage() {
    setLocalStorage("cart", {
      data: store.getters["cart/getCartData"],
      packeta: store.getters["packeta/getData"],
      discountCoupon: store.getters["discountCoupon/getData"],
      orderServices: store.getters["orderService/getOrderServices"], // [PACKAGE] - order service
    });
  }

  function loadCartFromLocalStorage() {
    const cart = getLocalStorage("cart");
    // load data from localstorage
    if (cart !== null) {
      store.commit("cart/SET_CART_DATA", cart.data);
      store.commit("packeta/SET_DATA", cart.packeta);
      store.commit("discountCoupon/SET_DATA", cart.discountCoupon);
      store.commit("orderService/SET_ORDER_SERVICES", cart.orderServices); // [PACKAGE] - order service
    }
  }

  function getCartProductByIndex(index = 0) {
    const cart = getLocalStorage("cart");

    return cart ? cart.data.products[index] : null;
  }

  // simple required validation form
  function deliveryFieldValidation() {
    const { customer } = store.getters["cart/getCartData"];

    for (const fieldName in deliveryFields) {
      if (deliveryFields[fieldName].rules.includes("required")) {
        // has empty any required vields
        if (empty(customer[fieldName])) return false;
      }
    }

    return true;
  }

  function stepMiddleware(step) {
    if (step > 1 && !store.getters["cart/getCartData"].products.length) {
      // products
      return 1;
    }

    if (step > 2 && !deliveryFieldValidation()) {
      // delivery data
      return 2;
    }

    if (
      step > 3 &&
      (!store.getters["cart/getCartData"].shipping ||
        !store.getters["cart/getCartData"].payment)
    ) {
      // payment shipping
      return 3;
    }

    return step;
  }

  function updateCartStep(step) {
    step = stepMiddleware(step);

    store.commit("cart/SET_STEP", step);

    updateUrl(cartIndex(step));

    scrollToSection("#app");
  }

  function getActualStep() {
    const { pathname } = location;
    const params = pathname.split("/");

    return Number(params[2]);
  }

  async function setPayments() {
    const dispatchMethod = "cart/GET_PAYMENTS";
    const commitMethod = "cart/SET_PAYMENTS";

    await getData(dispatchMethod, commitMethod);
  }

  async function setShippings() {
    const dispatchMethod = "cart/GET_SHIPPINGS";
    const commitMethod = "cart/SET_SHIPPINGS";

    await getData(dispatchMethod, commitMethod);
  }

  function setFreeShippingLimit(value) {
    const commitMethod = "cart/SET_FREE_SHIPPING_LIMIT";

    store.commit(commitMethod, value);
  }

  function setHasFreeShipping(status) {
    const commitMethod = "cart/SET_HAS_FREE_SHIPPING";

    store.commit(commitMethod, status);

    setCartLocalStorage();
  }

  function setLanguage(language) {
    const commitMethod = "cart/SET_LANGUAGE";

    store.commit(commitMethod, JSON.parse(language));
  }

  return {
    getImage,
    getActualStep,
    totalPrice,
    discountPrice,
    formattedPrice,
    productPrice,
    deleteProduct,
    setCartLocalStorage,
    setFreeShippingLimit,
    setHasFreeShipping,
    setShippings,
    setPayments,
    updateCartStep,
    getProductPrice,
    loadCartFromLocalStorage,
    layoutPhotoCount,
    getCartProductByIndex,
    setLanguage,
  };
}
